import z from 'zod';

import type { MediaStatus, MediaVariantDto, MediaVariants } from '@/common/media';
import type { List } from '@/utils/list';

/**
 * Four values since the document service's documents-refactor, and also the order
 * every listing comes back in. `special` and `system` are the two kinds of default
 * folder: `special` is crew working space and accepts uploads, `system` is fed by
 * another service and is read-only.
 *
 * Folder-ness is therefore "not FILE" — see {@link isFolderDocument}. A bare
 * `type === FOLDER` check reads Financial, Tasks and Crew as files.
 */
export enum DocumentType {
  SPECIAL = 'special',
  SYSTEM = 'system',
  FOLDER = 'folder',
  FILE = 'file',
}

/** Anything that can be opened and listed — every type except a file. */
export const isFolderDocument = (doc: { type: DocumentType; contentType?: null | string }) =>
  doc.type !== DocumentType.FILE || doc.contentType === 'directory';

/**
 * A folder from the service's own registry rather than one a person made:
 * `special` crew space, or a `system` folder another service fills.
 *
 * Only ever true at the top level — the registry's entries all live there, and a
 * container's per-element subfolders are written as plain `folder`s — so below
 * the root this is uniformly false.
 */
export const isBuiltInDocument = (doc: { type: DocumentType }) =>
  doc.type === DocumentType.SPECIAL || doc.type === DocumentType.SYSTEM;

export enum DocumentPermissionValue {
  READ = 'read',
  WRITE = 'write',
}

export type DocumentPermission = {
  permission: DocumentPermissionValue;
  role: string | null;
  person_id: string | null;
  system_permission: string | null;
};

export interface DocumentViewDto {
  document_id: string;
  id: string;
  time: string;
  user_id: string;
}

export interface DocumentView {
  id: string;
  documentId: string;
  userId: string;
  time: string;
}

export interface SingleDocumentDto {
  children_count: number;
  content_type: string;
  created_at: string;
  created_by: string;
  encryption: unknown;
  id: string;
  /**
   * A folder from the service's default registry: never movable, never deletable.
   * All three flags are `omitempty` on the wire — absent means false.
   */
  is_default?: boolean;
  /** Video and audio files only — see {@link MediaStatus}. */
  media_status?: MediaStatus;
  meta: unknown;
  name: string;
  parent_id: null | string;
  permissions: DocumentPermission[];
  /** The bytes live in another service's storage, so they cannot be replaced here. */
  read_only?: boolean;
  /** Non-null marks a pointer — a linked file, a shortcut or a shared-in mirror. */
  references: unknown;
  /**
   * Another service fills this folder — Wallet, Chat, Tasks, Events, Equipment.
   * Inherited by everything under it, and the successor to the old `source`
   * string: nothing may be created here by hand. Renaming is still ours, because
   * the name is ours.
   */
  service_managed?: boolean;
  size: number;
  storage_link: string;
  storage_name: string;
  type: DocumentType;
  updated_at: string;
  /** Absent when there are none. Unlinked: each one is `storage_link` + `/variants/<name>`. */
  variants?: Record<string, MediaVariantDto>;
  color: null | string;
  shared: string[];
  views: number;
}

export type DocumentsListDto = List<SingleDocumentDto>;

export interface SingleDocument {
  childrenCount: number;
  contentType: string;
  createdAt: string;
  createdBy: string;
  encryption: unknown;
  id: string;
  isDefault: boolean;
  mediaStatus: MediaStatus | null;
  meta: unknown;
  name: string;
  parentId: null | string;
  permissions: DocumentPermission[];
  readOnly: boolean;
  references: unknown;
  serviceManaged: boolean;
  size: number;
  storageLink: string;
  storageName: string;
  type: DocumentType;
  updatedAt: string;
  variants: MediaVariants;
  color: null | string;
  shared: string[];
  views: number;
}

export type DocumentsList = List<SingleDocument>;

/**
 * What a cursor listing may narrow by — and deliberately not how it is ordered.
 *
 * `GET /documents` refuses a cursor sent with `order_by` (`ErrCursorWithSort`):
 * only the default ordering has an index that can be resumed from. So a stream
 * takes the service's own order — `type DESC, id DESC` — and the absence of a
 * sort field here is the contract, not an omission.
 *
 * Every field is part of the cursor's fingerprint server-side, so changing one
 * mid-stream invalidates the cursor. React Query's key covers the same fields,
 * which is what makes that unreachable: a different filter is a different query.
 */
/**
 * What `GET /documents/recent` counts as recent.
 *
 * `FOLDERS` is "everything that is not a file" server-side — a `type > file`
 * comparison, so it takes in the built-in `system` and `special` folders too,
 * the same way {@link isFolderDocument} does here.
 */
export enum RecentDocumentsType {
  FILES = 'files',
  FOLDERS = 'folders',
  BOTH = 'both',
}

export interface DocumentsCursorFilters {
  search?: string;
  type?: DocumentType;
}

export interface DocumentsCursorPageDto {
  items: SingleDocumentDto[];
  /** Both are absent on the last step rather than null. */
  has_more?: boolean;
  next_cursor?: string;
}

/**
 * One step of the stream. Not a {@link List}: there is no `count`, because the
 * total is the expensive half of the request and the stream does not ask for it.
 * "Is there more" is `hasMore`, never arithmetic against a total.
 */
export interface DocumentsCursorPage {
  items: SingleDocument[];
  hasMore: boolean;
  nextCursor: null | string;
}

export const createDocumentsFolderSchema = z.object({
  name: z.string().min(1, 'Required field'),
  parentId: z.string().min(1, 'Required field').nullable(),
  color: z.string().nullable().optional(),
});

export type CreateDocumentsFolderFormValues = z.infer<typeof createDocumentsFolderSchema>;

export const renameDocumentSchema = z.object({
  id: z.string(),
  parentId: z.string().min(1, 'Required field').nullable(),
  name: z.string().min(1, 'Required field'),
  color: z.string().nullable().optional(),
});

export type RenameDucumentFormValues = z.infer<typeof renameDocumentSchema>;

export const uploadDocumentsFileSchema = z.object({
  file: z.instanceof(File, { error: 'Please choose a file.' }),
  parentId: z.string().min(1, 'Required field').nullable(),
  name: z.string().min(1, 'Please enter a file name.'),
  color: z.string().nullable().optional(),
});

export type UploadDocumentsFileFormValues = z.infer<typeof uploadDocumentsFileSchema>;
